import { createContext, useContext, useMemo, useState } from "react";
import { DEFAULT_RANGE_KEY, resolveRange, toInputDate } from "../utils/format";
import { buildPreviousPeriod } from "../utils/dateRange";

const ReportFiltersContext = createContext(null);

export const ReportFiltersProvider = ({ children, defaultShopId }) => {
  const [shopId, setShopId] = useState(defaultShopId || null);
  const [rangeKey, setRangeKey] = useState(DEFAULT_RANGE_KEY);
  const today = useMemo(() => toInputDate(new Date()), []);
  const weekAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return toInputDate(d);
  }, []);
  const [customFrom, setCustomFrom] = useState(weekAgo);
  const [customTo, setCustomTo] = useState(today);
  const [compareEnabled, setCompareEnabled] = useState(false);

  const range = useMemo(
    () => resolveRange(rangeKey, { customFrom, customTo }),
    [rangeKey, customFrom, customTo],
  );

  const previous = useMemo(
    () =>
      range && compareEnabled
        ? buildPreviousPeriod(range.from, range.to)
        : null,
    [range, compareEnabled],
  );

  const value = useMemo(
    () => ({
      shopId,
      setShopId,
      rangeKey,
      setRangeKey,
      customFrom,
      customTo,
      setCustomFrom,
      setCustomTo,
      from: range?.from || null,
      to: range?.to || null,
      isRangeValid: !!range,
      compareEnabled,
      setCompareEnabled,
      compareFrom: previous?.from || null,
      compareTo: previous?.to || null,
    }),
    [shopId, rangeKey, customFrom, customTo, range, compareEnabled, previous],
  );

  return (
    <ReportFiltersContext.Provider value={value}>
      {children}
    </ReportFiltersContext.Provider>
  );
};

export const useReportFilters = () => {
  const ctx = useContext(ReportFiltersContext);
  if (!ctx) {
    throw new Error("useReportFilters must be used inside ReportFiltersProvider");
  }
  return ctx;
};
